import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { motion } from "framer-motion";
import {
  User,
  Bell,
  Eye,
  Palette,
  Moon,
  Sun,
  Check,
  ChevronDown,
  MapPin,
  Settings as SettingsIcon,
  Crop,
  Calendar,
} from "lucide-react";
import MemberLayout from "@/layouts/MemberLayout";
import { InlinePhotoAdjuster } from "@/components/InlinePhotoAdjuster";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTheme } from "next-themes";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useForm, Controller } from "react-hook-form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { cn } from "@/lib/utils";
import { useGetLanguages, API_BASE } from "@/lib/api-client-react";

const GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say"];
const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
];

type FormData = {
  firstName: string;
  middleName: string;
  lastName: string;
  displayName: string;
  ssn: string;
  gender: string;
  dob: string;
  languages: string[];
  email: string;
  buildingNo: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  whatsapp: string;
  profilePicture?: string;
};

function MultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder,
  maxHeight = 260,
}: any) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const normalizedOptions = (options || []).map((o: any) => {
    if (typeof o === "string") return { value: o, label: o };
    if (typeof o === "object" && o !== null) {
      const val = String(o.value ?? o.id ?? o.name ?? o.label ?? "");
      const lbl = String(o.label ?? o.name ?? o.value ?? "");
      return { value: val, label: lbl };
    }
    return { value: String(o ?? ""), label: String(o ?? "") };
  });

  const filtered = normalizedOptions.filter((o) =>
    o.label.toLowerCase().includes((search || "").toLowerCase()),
  );

  const safeValue = Array.isArray(value) ? value : [];

  const toggle = (optValue: string) => {
    onChange(
      safeValue.includes(optValue)
        ? safeValue.filter((v: any) => v !== optValue)
        : [...safeValue, optValue],
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full min-h-[40px] px-3 py-2 border border-input rounded-md bg-background text-sm text-left flex flex-wrap gap-1.5 items-center hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 transition-colors"
        >
          {safeValue.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            safeValue.map((v: any) => {
              const label =
                normalizedOptions.find((o: any) => o.value === v)?.label || v;
              return (
                <Badge
                  key={v}
                  variant="secondary"
                  className="text-xs rounded-full px-2 py-0.5"
                >
                  {label}
                  <span
                    className="ml-1 cursor-pointer hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(v);
                    }}
                  >
                    ×
                  </span>
                </Badge>
              );
            })
          )}
          <ChevronDown className="w-4 h-4 ml-auto flex-shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        align="start"
      >
        <div className="p-2 border-b">
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
            autoFocus
          />
        </div>
        <div className="overflow-y-auto" style={{ maxHeight }}>
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No results
            </p>
          ) : (
            filtered.map((opt: any) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors text-left"
              >
                <div
                  className={cn(
                    "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                    safeValue.includes(opt.value)
                      ? "bg-primary border-primary"
                      : "border-input",
                  )}
                >
                  {safeValue.includes(opt.value) && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                {opt.label}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FieldLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <Label className="text-sm font-medium text-foreground">
      {children}
      {required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
  );
}

export default function MemberSettings() {
  const { theme, setTheme } = useTheme();
  const { toast } = useToast();
  const [emailNotif, setEmailNotif] = useState(true);
  const [smsNotif, setSmsNotif] = useState(true);
  const [apptReminders, setApptReminders] = useState(true);

  const { data: languagesData = [] } = useGetLanguages();
  const LANGUAGES = languagesData.map((l: any) => l.name);

  const form = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      firstName: "",
      middleName: "",
      lastName: "",
      displayName: "",
      ssn: "",
      gender: "",
      dob: "",
      languages: [],
      email: "",
      buildingNo: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      zipCode: "",
      phone: "",
      whatsapp: "",
    },
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = form;

  const [user, setUser] = useState<any>(null);
  const [pendingNav, setPendingNav] = useState<string | null>(null);
  const [, navigate] = useLocation();

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!isDirty) return;
      const target = e.target as HTMLElement;
      const link = target.closest("a");
      if (link && link.href) {
        if (!link.href.includes("/member/settings")) {
          e.preventDefault();
          e.stopPropagation();
          setPendingNav(link.getAttribute("href") || "");
        }
      }
    };
    document.addEventListener("click", handleClick, { capture: true });
    return () =>
      document.removeEventListener("click", handleClick, { capture: true });
  }, [isDirty]);

  useEffect(() => {
    const fetchUserData = async () => {
      // 1. Populate immediately from localStorage so form fields are never blank
      const localUserStr = localStorage.getItem("user");
      if (localUserStr) {
        try {
          const parsed = JSON.parse(localUserStr);
          setUser(parsed);
          let address: any = {};
          if (parsed.addresses && parsed.addresses.length > 0) {
            address = {
              buildingNo: parsed.addresses[0].buildingNo || "",
              street1: parsed.addresses[0].street1 || "",
              street2: parsed.addresses[0].street2 || "",
              city: parsed.addresses[0].city || "",
              state: parsed.addresses[0].state || "",
              zipCode: parsed.addresses[0].zipCode || "",
              phone: parsed.addresses[0].phone || "",
            };
          }
          reset({
            ...address,
            firstName: parsed.firstName || "",
            middleName: parsed.middleName || "",
            lastName: parsed.lastName || "",
            displayName: parsed.displayName || "",
            ssn: parsed.member?.ssn || "",
            gender: parsed.gender || "",
            dob: parsed.dob ? String(parsed.dob).split("T")[0] : "",
            languages: parsed.languages?.map((l: any) => l.name || l) || [],
            email: parsed.email || "",
            whatsapp: parsed.whatsapp || "",
            profilePicture: parsed.profilePicture || "",
            phone: parsed.phone || address.phone || "",
          });
        } catch (e) {}
      }

      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const res = await fetch(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();

        if (json.success) {
          const parsed = json.data;
          setUser(parsed);
          localStorage.setItem("user", JSON.stringify(parsed));

          let address: any = {};
          if (parsed.addresses && parsed.addresses.length > 0) {
            address = {
              buildingNo: parsed.addresses[0].buildingNo || "",
              street1: parsed.addresses[0].street1 || "",
              street2: parsed.addresses[0].street2 || "",
              city: parsed.addresses[0].city || "",
              state: parsed.addresses[0].state || "",
              zipCode: parsed.addresses[0].zipCode || "",
              phone: parsed.addresses[0].phone || "",
            };
          }

          reset({
            ...address,
            firstName: parsed.firstName || "",
            middleName: parsed.middleName || "",
            lastName: parsed.lastName || "",
            displayName: parsed.displayName || "",
            ssn: parsed.member?.ssn || "",
            gender: parsed.gender || "",
            dob: parsed.dob ? String(parsed.dob).split("T")[0] : "",
            languages: parsed.languages?.map((l: any) => l.name || l) || [],
            email: parsed.email || "",
            whatsapp: parsed.whatsapp || "",
            profilePicture: parsed.profilePicture || "",
            phone: parsed.phone || address.phone || "",
          });
        }
      } catch (e) {
        console.error("Failed to fetch user settings:", e);
      }
    };
    fetchUserData();
  }, [reset]);

  const displayName =
    watch("displayName") ||
    (watch("firstName") ? `${watch("firstName")} ${watch("lastName")}` : "");
  const fallbackChars = displayName
    ? displayName.substring(0, 2).toUpperCase()
    : "M";

  const formatSSN = (val: string) => {
    const cleaned = val.replace(/\D/g, "").slice(0, 9);
    let res = "";
    if (cleaned.length > 0) res += cleaned.slice(0, 3);
    if (cleaned.length > 3) res += "-" + cleaned.slice(3, 5);
    if (cleaned.length > 5) res += "-" + cleaned.slice(5, 9);
    return res;
  };

  const handleSave = async (data: FormData) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/members/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      const json = await res.json();

      if (json.success) {
        toast({
          title: "Settings saved",
          description: "Your profile settings have been updated.",
        });
        setUser(json.data);
        reset(data); // Reset form state so isDirty becomes false
      } else {
        throw new Error(json.error || "Failed to update");
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to save settings",
        variant: "destructive",
      });
    }
  };

  const [isPhotoAdjusterOpen, setIsPhotoAdjusterOpen] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState<string>("");
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        setTempImageSrc(reader.result as string);
        setIsPhotoAdjusterOpen(true);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleAdjustExistingPhoto = () => {
    const currentPic = watch("profilePicture");
    if (!currentPic) return;
    const fullUrl = currentPic.startsWith("http")
      ? currentPic
      : `${API_BASE.replace(/\/v1|\/api/, "")}${currentPic.startsWith("/") ? "" : "/"}${currentPic}`;
    setTempImageSrc(fullUrl);
    setIsPhotoAdjusterOpen(true);
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    setIsPhotoUploading(true);
    const formData = new FormData();
    formData.append("avatar", croppedBlob, "profile-avatar.jpg");

    try {
      const res = await fetch(`${API_BASE}/upload/avatar`, {
        method: "POST",
        body: formData,
      });
      const json = await res.json();

      if (json.success) {
        toast({
          title: "Photo Updated",
          description:
            "Centered profile photo saved. Remember to save all changes.",
        });
        form.setValue("profilePicture", json.url, { shouldDirty: true });
        setIsPhotoAdjusterOpen(false);
      } else {
        throw new Error(json.error || "Upload failed");
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to upload photo",
        variant: "destructive",
      });
    } finally {
      setIsPhotoUploading(false);
    }
  };

  return (
    <MemberLayout>
      <div className="w-full space-y-6 sm:space-y-7 pt-2 sm:pt-4 pb-16">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-border/50">
          <div className="flex items-center gap-3.5">
            <div className="p-2.5 sm:p-3 bg-primary/10 rounded-xl flex-shrink-0 text-primary border border-primary/20 shadow-xs">
              <SettingsIcon className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">
              Account Settings
            </h1>
          </div>
          <Button
            onClick={handleSubmit(handleSave)}
            className="w-full sm:w-auto h-10 sm:h-11 px-5 sm:px-6 rounded-xl font-semibold bg-gradient-to-r from-primary to-secondary text-white border-0 shadow-sm transition-all"
            disabled={!isDirty}
          >
            Save All Changes
          </Button>
        </div>

        <Tabs defaultValue="profile">
          <div className="flex justify-center lg:justify-start mb-6">
            <TabsList className="bg-muted/70 p-1 rounded-xl flex-wrap h-auto gap-1 justify-center">
              {[
                { id: "profile", icon: User, label: "Profile" },
                { id: "address", icon: MapPin, label: "Address" },
                // { id: "notifications", icon: Bell, label: "Notifications" },
                // { id: "privacy", icon: Eye, label: "Privacy" },
                { id: "appearance", icon: Palette, label: "Appearance" },
              ].map((s) => (
                <TabsTrigger
                  key={s.id}
                  value={s.id}
                  className="rounded-lg gap-2 text-xs sm:text-sm px-3.5 py-1.5 font-medium"
                  data-testid={`tab-${s.id}`}
                >
                  <s.icon className="w-4 h-4" />
                  {s.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <form onSubmit={handleSubmit(handleSave)}>
            <TabsContent value="profile">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-card rounded-2xl border border-border p-5 sm:p-6 space-y-5 shadow-xs"
              >
                <div className="flex items-center gap-4 pb-4 border-b border-border/60">
                  <Avatar className="w-16 h-16 shrink-0">
                    {watch("profilePicture") ? (
                      <img
                        src={`${API_BASE.replace(/\/v1|\/api/, "")}${watch("profilePicture")}`}
                        alt="Profile"
                        className="object-cover w-full h-full"
                      />
                    ) : (
                      <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white text-xl font-bold">
                        {fallbackChars}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-semibold text-lg text-foreground">
                      {displayName || "Member"}
                    </p>
                    <p className="text-sm text-muted-foreground mb-2 font-medium">
                      Patient Account
                    </p>
                    {user?.member?.id && (
                      <p className="text-xs font-mono text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded w-fit mb-2 border border-indigo-200/50 dark:border-indigo-800/50 font-medium">
                        ID: DC-{user.member.id.substring(0, 8).toUpperCase()}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <input
                        type="file"
                        id="photo-upload"
                        className="hidden"
                        accept="image/*"
                        onChange={handlePhotoSelect}
                      />
                      <Label
                        htmlFor="photo-upload"
                        className="inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground h-8 rounded-lg px-3 text-xs cursor-pointer"
                      >
                        Change Photo
                      </Label>
                      {watch("profilePicture") && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleAdjustExistingPhoto}
                          className="h-8 rounded-lg px-3 text-xs gap-1 border-primary/30 text-primary hover:bg-primary/5"
                        >
                          <Crop className="w-3.5 h-3.5" /> Adjust Position &
                          Center
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {isPhotoAdjusterOpen && (
                  <InlinePhotoAdjuster
                    imageSrc={tempImageSrc}
                    onSave={handleCropComplete}
                    onCancel={() => setIsPhotoAdjusterOpen(false)}
                    isUploading={isPhotoUploading}
                  />
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
                  <div>
                    <FieldLabel>First Name</FieldLabel>
                    <Input
                      {...register("firstName")}
                      className="mt-1.5 h-10 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <FieldLabel>Middle Name</FieldLabel>
                    <Input
                      {...register("middleName")}
                      className="mt-1.5 h-10 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <FieldLabel>Last Name</FieldLabel>
                    <Input
                      {...register("lastName")}
                      className="mt-1.5 h-10 rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
                  <div>
                    <FieldLabel>Display Name</FieldLabel>
                    <Input
                      {...register("displayName")}
                      className="mt-1.5 h-10 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <FieldLabel>Date of Birth</FieldLabel>
                    <div className="relative mt-1.5">
                      <Input
                        type="date"
                        {...register("dob")}
                        className="h-10 rounded-lg text-sm pr-10 cursor-pointer block w-full dark:[color-scheme:dark] [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                        onClick={(e) => {
                          try {
                            e.currentTarget.showPicker?.();
                          } catch (_) {}
                        }}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground flex items-center">
                        <Calendar className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <FieldLabel>Gender</FieldLabel>
                    <Controller
                      control={control}
                      name="gender"
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || ""}
                        >
                          <SelectTrigger className="mt-1.5 h-10 rounded-lg text-sm">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {GENDERS.map((g) => (
                              <SelectItem key={g} value={g}>
                                {g}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
                  <div>
                    <FieldLabel>SSN</FieldLabel>
                    <Controller
                      name="ssn"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="XXX-XX-XXXX"
                          className="mt-1.5 h-10 rounded-lg text-sm"
                          onChange={(e) =>
                            field.onChange(formatSSN(e.target.value))
                          }
                          maxLength={11}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <FieldLabel>Languages Spoken</FieldLabel>
                    <div className="mt-1.5">
                      <Controller
                        control={control}
                        name="languages"
                        render={({ field }) => (
                          <MultiSelect
                            options={LANGUAGES}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select languages..."
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div>
                    <FieldLabel>Email</FieldLabel>
                    <Input
                      value={user?.email || ""}
                      disabled
                      className="mt-1.5 h-10 rounded-lg bg-slate-50/50 dark:bg-slate-900/50 text-slate-900 dark:text-white disabled:opacity-100 cursor-not-allowed text-sm"
                    />
                  </div>
                </div>

                <div className="border-t border-border/60 pt-4 mt-2">
                  <h3 className="font-semibold text-sm sm:text-base mb-3">
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel>Phone Number</FieldLabel>
                      <Input
                        {...register("phone")}
                        onInput={(e) => {
                          e.currentTarget.value = e.currentTarget.value.replace(
                            /[a-zA-Z]/g,
                            "",
                          );
                        }}
                        type="tel"
                        placeholder="(555) 123-4567"
                        className="mt-1.5 h-10 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <FieldLabel>WhatsApp Number</FieldLabel>
                      <Input
                        {...register("whatsapp")}
                        onInput={(e) => {
                          e.currentTarget.value = e.currentTarget.value.replace(
                            /[a-zA-Z]/g,
                            "",
                          );
                        }}
                        type="tel"
                        placeholder="(555) 123-4567"
                        className="mt-1.5 h-10 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </TabsContent>

            <TabsContent value="address">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-card rounded-2xl border border-border p-5 sm:p-6 space-y-4 shadow-xs"
              >
                <h2 className="font-semibold text-base mb-2">
                  Address Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <FieldLabel>Building No.</FieldLabel>
                    <Input
                      {...register("buildingNo")}
                      className="mt-1.5 h-10 rounded-lg text-sm"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <FieldLabel>Street Name 1</FieldLabel>
                    <Input
                      {...register("street1")}
                      className="mt-1.5 h-10 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 mb-4">
                  <div>
                    <FieldLabel>Street Name 2</FieldLabel>
                    <Input
                      {...register("street2")}
                      className="mt-1.5 h-10 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <FieldLabel>City</FieldLabel>
                    <Input
                      {...register("city")}
                      className="mt-1.5 h-10 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <FieldLabel>State</FieldLabel>
                    <Controller
                      control={control}
                      name="state"
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || ""}
                        >
                          <SelectTrigger className="mt-1.5 h-10 rounded-lg text-sm">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {US_STATES.map((s) => (
                              <SelectItem key={s} value={s}>
                                {s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div>
                    <FieldLabel>Zip Code</FieldLabel>
                    <Input
                      {...register("zipCode")}
                      className="mt-1.5 h-10 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </motion.div>
            </TabsContent>

            {/* <TabsContent value="notifications">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-card rounded-2xl border border-border p-6 space-y-4">
                <h2 className="font-semibold">Notification Preferences</h2>
                {[
                  { label: "Email Notifications", desc: "Get updates in your inbox", value: emailNotif, onChange: setEmailNotif, id: "email-notif" },
                  { label: "SMS Alerts", desc: "Receive text message reminders", value: smsNotif, onChange: setSmsNotif, id: "sms-notif" },
                  { label: "Appointment Reminders", desc: "24h reminder before your visit", value: apptReminders, onChange: setApptReminders, id: "appt-reminders" },
                ].map(item => (
                  <div key={item.id} className="flex items-center justify-between py-4 border-b border-border last:border-0">
                    <div>
                      <p className="font-medium text-sm">{item.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                    </div>
                    <Switch checked={item.value} onCheckedChange={item.onChange} data-testid={`switch-${item.id}`} />
                  </div>
                ))}
              </motion.div>
            </TabsContent> */}

            {/* <TabsContent value="privacy">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-card rounded-2xl border border-border p-6 space-y-4">
                <h2 className="font-semibold">Privacy Settings</h2>
                <p className="text-sm text-muted-foreground mb-4">Control how your data is shared and used.</p>
                {[
                  { label: "Share data with providers", desc: "Allows your care team to access your health records" },
                  { label: "Anonymized analytics", desc: "Help improve StraightDoc with anonymous usage data" },
                  { label: "Marketing communications", desc: "Receive health tips and product updates" },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between py-4 border-b border-border last:border-0">
                    <div className="pr-4">
                      <p className="font-medium text-sm">{item.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                    </div>
                    <Switch defaultChecked data-testid={`switch-${item.label.replace(/ /g, "-").toLowerCase()}`} />
                  </div>
                ))}
              </motion.div>
            </TabsContent> */}

            <TabsContent value="appearance">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-card rounded-2xl border border-border p-5 sm:p-6 space-y-5 shadow-xs"
              >
                <h2 className="font-semibold text-base">Appearance</h2>
                <div>
                  <p className="text-sm font-medium mb-3">Theme</p>
                  <div className="grid grid-cols-2 gap-4 max-w-xs sm:max-w-sm">
                    {[
                      {
                        value: "light",
                        icon: Sun,
                        label: "Light",
                        preview: "bg-white border-slate-200",
                      },
                      {
                        value: "dark",
                        icon: Moon,
                        label: "Dark",
                        preview: "bg-slate-900 border-slate-700",
                      },
                    ].map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setTheme(t.value)}
                        className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2.5 transition-all ${
                          theme === t.value
                            ? "border-primary bg-primary/5 shadow-xs"
                            : "border-border hover:border-primary/30"
                        }`}
                        data-testid={`button-theme-${t.value}`}
                      >
                        <div
                          className={`w-8 h-8 rounded-lg border ${t.preview} flex items-center justify-center`}
                        >
                          <t.icon className="w-4 h-4 text-slate-600" />
                        </div>
                        <span
                          className={`text-xs font-semibold ${theme === t.value ? "text-primary" : "text-muted-foreground"}`}
                        >
                          {t.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            </TabsContent>
          </form>
        </Tabs>
      </div>

      <ConfirmDialog
        open={!!pendingNav}
        onOpenChange={(open) => !open && setPendingNav(null)}
        title="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to discard them and leave this page?"
        confirmText="Discard Changes"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={() => {
          if (pendingNav) {
            reset();
            setTimeout(() => {
              navigate(pendingNav);
            }, 0);
          }
        }}
      />
    </MemberLayout>
  );
}
